// File: src/components/admin/plans/StripePricePicker.js
// searchable Stripe Price dropdown for the admin Plans editor.
//
// Replaces the free-text stripePriceId field with a dropdown sourced from
// `GET /admin/stripe/prices`. Picking a price auto-fills `price`, `currency`,
// `interval`, and `stripeProductId` on the plan editor draft.
// A "Verify" button hits `POST /admin/stripe/verify-price` and renders the
// result chip (green ok / red specific-reason).

import React, { useEffect, useState, useMemo } from 'react';
import {
    Stack, TextField, Autocomplete, Button, Chip, FormControlLabel,
    Switch, CircularProgress, Tooltip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { mainApi } from '../../../services/mainApi';

const REASONS = {
    not_found: 'Stripe Price not found',
    archived: 'Stripe Price is archived',
    product_mismatch: 'Price belongs to a different Product',
    currency_mismatch: 'Price currency does not match plan currency',
    amount_mismatch: 'Price amount does not match plan price',
};

function formatPriceLabel(p) {
    const amount = (Number(p.unitAmount || 0) / 100).toFixed(2);
    const cur = String(p.currency || '').toUpperCase();
    const nick = p.nickname || p.lookupKey || p.id;
    return `${nick} — ${cur} ${amount}/${p.interval || '?'} · ${p.productName || p.productId || ''}`;
}

export default function StripePricePicker({
    planId, value, currency, expectedAmount, decoupled, onChange, onDecouple, onPickedAutoFill,
}) {
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [error, setError] = useState(null);

    async function loadPrices() {
        setLoading(true);
        setError(null);
        try {
            const data = await mainApi.listStripePrices({ active: true });
            setPrices(data.prices || []);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadPrices(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const selected = useMemo(
        () => prices.find(p => p.id === value) || null,
        [prices, value]
    );

    async function handleVerify() {
        if (!value) return;
        setVerifying(true);
        setVerifyResult(null);
        try {
            const result = await mainApi.verifyStripePrice({
                priceId: value,
                currency,
                expectedAmount: expectedAmount != null ? Number(expectedAmount) : undefined,
            });
            setVerifyResult(result);
        } catch (e) {
            setVerifyResult({ ok: false, reason: 'error', error: e?.response?.data?.error || e.message });
        } finally {
            setVerifying(false);
        }
    }

    function handlePick(_event, opt) {
        if (!opt) {
            onChange('');
            setVerifyResult(null);
            return;
        }
        onChange(opt.id);
        setVerifyResult(null);
        if (onPickedAutoFill && !decoupled) {
            onPickedAutoFill({
                stripeProductId: opt.productId || '',
                price: opt.unitAmount ?? undefined,
                currency: (opt.currency || '').toUpperCase(),
                interval: opt.interval || undefined,
            });
        }
    }

    return (
        <Stack spacing={1} data-testid={`stripe-picker-${planId}`}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Autocomplete
                    sx={{ flex: 1 }}
                    options={prices}
                    value={selected}
                    onChange={handlePick}
                    getOptionLabel={formatPriceLabel}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    loading={loading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Stripe Price"
                            size="small"
                            inputProps={{
                                ...params.inputProps,
                                'data-testid': `stripe-price-input-${planId}`,
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress size={16} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Tooltip title="Refresh from Stripe">
                    <span>
                        <Button size="small" onClick={loadPrices} disabled={loading}>
                            <RefreshIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    size="small" variant="outlined"
                    onClick={handleVerify}
                    disabled={!value || verifying}
                    data-testid={`stripe-verify-${planId}`}
                >
                    {verifying ? <CircularProgress size={16} /> : 'Verify'}
                </Button>
            </Stack>

            {value && !selected && !loading && (
                <Chip
                    color="warning"
                    size="small"
                    label={`Price "${value}" not in active list — click Verify`}
                    data-testid={`stripe-warn-not-listed-${planId}`}
                />
            )}

            {verifyResult && verifyResult.ok && (
                <Chip
                    icon={<VerifiedIcon />}
                    color="success"
                    size="small"
                    label="Verified — matches plan price and currency"
                    data-testid={`stripe-verify-ok-${planId}`}
                />
            )}
            {verifyResult && !verifyResult.ok && (
                <Chip
                    icon={<ErrorIcon />}
                    color="error"
                    size="small"
                    label={REASONS[verifyResult.reason] || verifyResult.error || 'Verification failed'}
                    data-testid={`stripe-verify-fail-${planId}`}
                />
            )}

            {error && (
                <Chip color="error" size="small" label={`Stripe list failed: ${error}`} />
            )}

            <FormControlLabel
                control={<Switch
                    size="small"
                    checked={!!decoupled}
                    onChange={(e) => onDecouple(e.target.checked)}
                    data-testid={`stripe-decouple-${planId}`}
                />}
                label="Allow display price to differ from Stripe"
            />
            {decoupled && (
                <Chip
                    color="warning"
                    size="small"
                    label="Display price decoupled from Stripe — values may not match"
                    data-testid={`stripe-decoupled-warning-${planId}`}
                />
            )}
        </Stack>
    );
}
